import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, fetchFormToken, fetchWebinar, register, type Project, type WebinarOverview } from '../api';
import { formatSession } from '../format';

interface Props {
  project: Project;
  slug: string;
}

const failureText: Record<string, string> = {
  rate_limited: 'Zu viele Anmeldungen in kurzer Zeit. Bitte versuch es in einer Stunde noch einmal.',
  rejected: 'Die Anmeldung konnte nicht angenommen werden. Bitte lade die Seite neu und versuch es erneut.',
  invalid_email: 'Bitte prüfe die E-Mail-Adresse – so kann sie nicht stimmen.',
  unknown_domain: 'Unter dieser Domain kommen keine E-Mails an. Hast du dich vertippt?',
  invalid_session: 'Dieser Termin wird nicht mehr angeboten. Bitte wähle einen anderen.',
  no_session: 'Bitte wähle zuerst einen Termin aus.',
  unknown_webinar: 'Dieses Webinar gibt es nicht mehr.',
};

export function RegisterPage({ project, slug }: Props) {
  const [webinar, setWebinar] = useState<WebinarOverview | null | undefined>(undefined);
  const [formToken, setFormToken] = useState<string>('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [session, setSession] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ token: string; sessionStartsAt: string } | null>(null);

  useEffect(() => {
    fetchWebinar(slug)
      .then((w) => {
        setWebinar(w);
        document.title = `${w.title} – ${project.name}`;
        // Genau ein Termin: gleich gewählt. Mehrere: die Auswahl bleibt leer, bis die Person sich entscheidet.
        if (w.sessions.length === 1) setSession(w.sessions[0]!.startsAt);
      })
      .catch(() => setWebinar(null));
    fetchFormToken()
      .then((t) => setFormToken(t.token))
      .catch(() => undefined);
  }, [slug, project.name]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!session) {
      setError(failureText.no_session!);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await register(slug, {
        email,
        ...(name.trim() ? { name: name.trim() } : {}),
        sessionStartsAt: session,
        formToken,
        website,
        ...(newsletter ? { newsletter: true } : {}),
      });
      setDone(result);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      setError(failureText[code] ?? 'Das hat leider nicht geklappt. Bitte versuch es gleich noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  if (webinar === undefined) return null;
  if (webinar === null) {
    return (
      <>
        <Header project={project} />
        <main className="page page--message">
          <h1>Dieses Webinar gibt es nicht</h1>
          <p className="muted">Bitte prüfe den Link, den du bekommen hast.</p>
        </main>
      </>
    );
  }

  if (done) {
    const roomUrl = `${window.location.origin}/raum/${done.token}`;
    return (
      <>
        <Header project={project} />
        <main className="page">
          <h1>Du bist dabei</h1>
          <div className="card card--brand">
            <p className="label label--brand">Dein Termin</p>
            <p style={{ fontSize: 22, fontWeight: 600 }}>{formatSession(done.sessionStartsAt, project.timezone)}</p>
            <p className="muted">{webinar.title}</p>
          </div>
          <div className="card">
            <p className="label">Dein Zugang zum Webinar-Raum</p>
            <p>
              <a href={roomUrl}>{roomUrl}</a>
            </p>
            <p className="muted small">
              Der Link ist persönlich und gilt für ein Gerät. Speichere ihn dir – du kannst den Raum schon vor dem Start öffnen und dort warten.
            </p>
          </div>
          {newsletter && (
            <div className="card">
              <p className="label">Newsletter</p>
              <p className="muted small">Du bekommst gleich eine E-Mail von {project.name} – bitte bestätige dort mit einem Klick, dass du den Newsletter möchtest. Erst dann bist du eingetragen.</p>
            </div>
          )}
        </main>
      </>
    );
  }

  return (
    <>
      <Header project={project} />
      <main className="page">
        <div>
          <p className="label label--brand">Anmeldung</p>
          <h1 style={{ fontSize: 30, marginTop: 8 }}>{webinar.title}</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            Dauer etwa {Math.round(webinar.durationSeconds / 60)} Minuten. Aufzeichnung, gezeigt zu festen Terminen.
          </p>
        </div>

        <form className="card" onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="session">Wann möchtest du dabei sein?</label>
            {webinar.sessions.length === 0 ? (
              <p className="muted">Derzeit werden keine Termine angeboten.</p>
            ) : (
              <select
                id="session"
                name="session"
                required
                value={session}
                onChange={(e) => {
                  setSession(e.target.value);
                  setError(null);
                }}
              >
                <option value="" disabled>
                  Bitte Termin auswählen
                </option>
                {webinar.sessions.map((s) => (
                  <option key={s.startsAt} value={s.startsAt}>
                    {formatSession(s.startsAt, project.timezone)}
                    {s.kind === 'next_round' ? ' · nächste Vorführung' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="field">
            <label htmlFor="name">Dein Vorname</label>
            <input id="name" type="text" autoComplete="given-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="email">Deine E-Mail-Adresse</label>
            <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {project.newsletter && (
            <label className="choice">
              <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} />
              <span>
                Ja, ich möchte außerdem den Newsletter von {project.name} bekommen. <span className="muted small">Ich bestätige das gleich per E-Mail; abbestellen geht jederzeit.</span>
              </span>
            </label>
          )}

          <div className="hp" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input id="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          {error && <div className="error">{error}</div>}

          <button type="submit" className="button" disabled={busy || webinar.sessions.length === 0}>
            {busy ? 'Einen Moment …' : 'Platz sichern'}
          </button>
          <p className="muted small">Du bekommst deinen persönlichen Zugang sofort angezeigt. {project.newsletter ? 'Newsletter nur mit deinem Häkchen und deiner Bestätigung.' : 'Kein Newsletter.'} Keine Weitergabe deiner Daten.</p>
        </form>
      </main>
    </>
  );
}

export function Header({ project, right }: { project: Project; right?: React.ReactNode }) {
  return (
    <header className="header">
      {project.theme.logoUrl ? <img className="header__logo" src={project.theme.logoUrl} alt={project.name} /> : <span className="header__name">{project.name}</span>}
      <span className="header__spacer" />
      {right}
    </header>
  );
}
